// ./src/components/features/CreateEventWizard/steps/WhereStep.tsx
//
// Step 3 — "Where's it happening?". Format (in person / online / hybrid) drives
// which inputs show: a venue picker (reusing the Venue entity, with in-place
// create + a location map) for in-person/hybrid, and an online meeting link for
// online/hybrid. Uses the shared event controls + field family so it stays in
// lockstep with the event details editor.

import React, { useCallback, useEffect } from 'react';
import { Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { VenuesProvider, VenueCategoriesProvider, useVenuesContext } from '@/contexts';
import { VENUE_KIND, Venue, WHOLE_SET } from '@/utils/api';
import { AppIcon, TextField } from '@/components/generic';
import { EventFormatField } from '@/components/features/Events';

import { getFieldError, venueCapacityPatch } from '@/utils';

import { useEventWizard } from '../WizardContext';
import { VenuePickerField, formatVenueAddress } from '../components/VenuePickerField';
import { EVENT_FORMAT, EventWizardDraft } from '../wizardTypes';

/**
 * Fills the capacity for a venue the host did NOT pick in this step.
 *
 * ⚠⚠ THE PICKER'S onChange ONLY FIRES WHEN A HUMAN CHANGES IT, and there are two ways a venue gets
 * onto a draft without that happening: the account's default venue seeding a fresh wizard (A2b #53),
 * and a week-old draft being restored. The owner asked for the capacity to follow the venue
 * "by default or manually" — without this, "by default" silently did nothing, which is the same
 * shape as a preference nothing consumes.
 *
 * ⚠ It must live INSIDE `VenuesProvider` (that is where the venue rows are) and it renders nothing.
 * ⚠ `venueCapacityPatch` returning null for "no change" is what keeps this from looping: once the
 * capacity matches the venue, the effect stops patching.
 */
const VenueCapacitySync: React.FC = () => {
  const { draft, patchDraft } = useEventWizard();
  const { venues } = useVenuesContext();

  useEffect(() => {
    if (!draft.venueId) {
      return;
    }

    const venue = venues.find((v) => v.id === draft.venueId);

    if (!venue) {
      return;
    }

    const patch = venueCapacityPatch(
      { capacity: draft.capacity, capacityFromVenueId: draft.capacityFromVenueId },
      venue,
    );

    if (patch) {
      patchDraft(patch);
    }
  }, [venues, draft.venueId, draft.capacity, draft.capacityFromVenueId, patchDraft]);

  return null;
};

export const WhereStep: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  // ⓘ `saveError` — the server's refusal of a save (the essentials, or a quick Finish) is shown under the input it names.
  const { draft, patchDraft, saveError } = useEventWizard();

  const showVenue = draft.eventFormat === EVENT_FORMAT.IN_PERSON || draft.eventFormat === EVENT_FORMAT.HYBRID;
  const showOnline = draft.eventFormat === EVENT_FORMAT.ONLINE || draft.eventFormat === EVENT_FORMAT.HYBRID;

  // One stable handler for every plain field on this step.
  const handleChange = useCallback(
    (name: string | undefined, value: unknown) => {
      if (name) patchDraft({ [name]: value } as Partial<EventWizardDraft>);
    },
    [patchDraft],
  );

  // Venue emits a Venue object; cache name + address for the review summary.
  //
  // ⚠ It also ANSWERS THE CAPACITY QUESTION (owner, 2026-09-26): a venue knows how many people fit in
  // it, so picking one fills the limit in — and the Guests step's "Limit the number of guests" switch
  // derives from `capacity != null`, so it comes on by itself. The rule lives in `venueCapacityPatch`
  // because the event details editor has to behave identically, and it is careful never to overwrite
  // a number the host typed.
  const handleVenueChange = useCallback(
    (venue: Venue | null) => {
      const capacity = venueCapacityPatch(
        { capacity: draft.capacity, capacityFromVenueId: draft.capacityFromVenueId },
        venue,
      );

      patchDraft({
        venueId: venue?.id ?? null,
        venueName: venue?.name ?? null,
        venueAddress: formatVenueAddress(venue?.primaryAddress),
        ...(capacity ?? {}),
      });
    },
    [patchDraft, draft.capacity, draft.capacityFromVenueId],
  );

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Text fw={600} size="md">
          {t('events:wizard.where.formatLabel', { defaultValue: 'How is it hosted?' })}
        </Text>
        <EventFormatField
          name="eventFormat"
          value={draft.eventFormat}
          onChange={handleChange}
          fullWidth
        />
      </Stack>

      {showVenue && (
        <VenuesProvider pageSize={WHOLE_SET} initialFilters={{ kind: [VENUE_KIND.VENUE] }}>
          {/* VenueCategoriesProvider feeds the category picker inside the in-place
              "Add a new venue" drawer — without it the category list is empty. */}
          <VenueCategoriesProvider>
            <VenueCapacitySync />
            <VenuePickerField value={draft.venueId} onChange={handleVenueChange} error={getFieldError(saveError, 'venueId')} />
          </VenueCategoriesProvider>
        </VenuesProvider>
      )}

      {showOnline && (
        <TextField
          name="onlineMeetingUrl"
          label={t('events:wizard.where.onlineUrl', { defaultValue: 'Meeting link' })}
          description={t('events:wizard.where.onlineUrlHint', {
            defaultValue: 'Zoom, Google Meet, Teams, or any join URL',
          })}
          placeholder="https://…"
          value={draft.onlineMeetingUrl}
          onChange={handleChange}
          leftSection={<AppIcon iconName="Video" size={16} />}
          type="url"
          inputMode="url"
          error={getFieldError(saveError, 'onlineMeetingUrl')}
        />
      )}
    </Stack>
  );
};
